import {
  ROLE_INTERVIEW_PANEL,
  ROLE_TALENT_ACQUISITION,
  ROLE_HR,
  ROLE_APPLICATION_MANAGER,
  ROLE_DEPARTMENT_HEAD,
} from "../helpers/constants";
import { matchPath } from "react-router-dom";

// menuItem.showInMainNav: false = only show when in a parent's subMenu; true (default) = show in main nav
// menuItem.subMenu: array of paths (string) or { path, order? }; title/icon/permissions from route config
export const ROUTE_CONFIG = {
  "/applications": {
    allowedRoles: [ROLE_TALENT_ACQUISITION, ROLE_APPLICATION_MANAGER],
    component: "Applications",
    layout: "MainLayout",
    menuItem: {
      title: "Applications",
      icon: "ArticleIcon",
      order: 1,
      subMenu: ["/spam"],
    },
  },
  "/spam": {
    allowedRoles: [ROLE_TALENT_ACQUISITION, ROLE_APPLICATION_MANAGER],
    component: "Spam",
    layout: "MainLayout",
    menuItem: {
      title: "Spam",
      icon: "ReportIcon",
      order: 1,
      showInMainNav: false,
    },
  },
  "/candidates": {
    allowedRoles: [ROLE_TALENT_ACQUISITION],
    component: "Candidates",
    layout: "MainLayout",
    menuItem: {
      title: "Candidates",
      icon: "GroupsIcon",
      order: 3,
    },
  },
  "/create-candidate": {
    allowedRoles: [ROLE_TALENT_ACQUISITION],
    component: "CreateCandidate",
    layout: "MainLayout",
  },
  "/interviews": {
    allowedRoles: [
      ROLE_TALENT_ACQUISITION,
      ROLE_HR,
      ROLE_INTERVIEW_PANEL,
      ROLE_DEPARTMENT_HEAD,
    ],
    component: "Interviews",
    layout: "MainLayout",
    menuItem: {
      title: "Interviews",
      icon: "ComputerIcon",
      order: 4,
    },
  },
  "/candidates/:id": {
    allowedRoles: [ROLE_TALENT_ACQUISITION, ROLE_HR],
    component: "CandidateProfile",
    layout: "MainLayout",
  },
  "/interviews/:id/evaluation": {
    allowedRoles: [
      ROLE_TALENT_ACQUISITION,
      ROLE_HR,
      ROLE_INTERVIEW_PANEL,
      ROLE_DEPARTMENT_HEAD,
    ],
    component: "EvaluationPage",
    layout: "MainLayout",
  },
};

export const INTERVIEW_PANEL_ROLES = [
  "Interview Panel",
  "HR",
  "Department Head",
  "Talent Acquisition",
  // Add new roles that can take interviews here
];

export const getInterviewPanelRoles = () => {
  return INTERVIEW_PANEL_ROLES;
};
// Helper functions
export const canAccessRoute = (path, role) => {
  for (const [routePath, routeConfig] of Object.entries(ROUTE_CONFIG)) {
    if (matchPath(routePath, path)) {
      return routeConfig.allowedRoles.includes(role);
    }
  }

  return false;
};

/**
 * Main nav: routes with menuItem that role can access and menuItem.showInMainNav !== false.
 * subMenu: paths (or { path, order? }); title/icon/permissions from route config.
 */
export const getMenuItems = role => {
  const entries = Object.entries(ROUTE_CONFIG).filter(
    ([, config]) => config.menuItem && config.allowedRoles.includes(role),
  );
  const pathToItem = new Map(
    entries.map(([path, config]) => [path, { path, ...config.menuItem }]),
  );

  const topLevel = entries
    .filter(([, config]) => config.menuItem.showInMainNav !== false)
    .map(([path]) => pathToItem.get(path))
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return topLevel.map(item => {
    const subMenu = item.subMenu || [];
    if (subMenu.length === 0) return item;
    const children = subMenu
      .map(s => {
        const path = typeof s === "string" ? s : s.path;
        const routeConfig = ROUTE_CONFIG[path];
        if (!routeConfig || !routeConfig.allowedRoles?.includes(role))
          return null;
        const menu = routeConfig.menuItem;
        const order = typeof s === "string" ? 0 : (s.order ?? menu?.order ?? 0);
        if (!menu) return null;
        return {
          path,
          title: menu.title,
          icon: menu.icon,
          order,
        };
      })
      .filter(Boolean)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return { ...item, children };
  });
};

export const getDefaultRoute = role => {
  const menuItems = getMenuItems(role);
  return menuItems.length > 0 ? menuItems[0].path : "/interviews";
};
